import React, { useState, useEffect, useCallback } from "react";
import { getJournalEntries, createJournalEntry, deleteJournalEntry } from "../lib/api";
import { Plus, Trash2, TrendingUp, TrendingDown, Eye, ShoppingCart, LogOut, Loader2, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTION_CONFIG = {
  buy:   { label: "Buy",   color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", icon: ShoppingCart },
  sell:  { label: "Sell",  color: "text-red-400",     bg: "bg-red-500/10",     border: "border-red-500/20",     icon: LogOut },
  hold:  { label: "Hold",  color: "text-amber-400",   bg: "bg-amber-500/10",   border: "border-amber-500/20",   icon: Eye },
  watch: { label: "Watch", color: "text-blue-400",    bg: "bg-blue-500/10",    border: "border-blue-500/20",    icon: Eye },
  exit:  { label: "Exit",  color: "text-orange-400",  bg: "bg-orange-500/10",  border: "border-orange-500/20",  icon: LogOut },
};

export default function JournalPage({ onStockClick }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ symbol: "", action: "buy", account_name: "", price: "", quantity: "", reasoning: "", tags: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getJournalEntries();
      setEntries(data.entries || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.symbol) return;
    setSubmitting(true);
    try {
      await createJournalEntry({
        symbol: form.symbol.toUpperCase(),
        action: form.action,
        account_name: form.account_name || undefined,
        price: form.price ? parseFloat(form.price) : undefined,
        quantity: form.quantity ? parseInt(form.quantity) : undefined,
        reasoning: form.reasoning || undefined,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : undefined,
      });
      setOpen(false);
      setForm({ symbol: "", action: "buy", account_name: "", price: "", quantity: "", reasoning: "", tags: "" });
      load();
    } catch {}
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    await deleteJournalEntry(id);
    load();
  };

  const filtered = filter === "all" ? entries : entries.filter(e => e.action === filter);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-[var(--brand)]" size={28} /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="journal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Trade Journal</h1>
          <p className="text-sm text-[var(--text-muted)]">{entries.length} entries</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-[var(--bg-surface)] border border-white/10 rounded-md p-0.5">
            {["all", "buy", "sell", "hold", "watch", "exit"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-2.5 py-1 text-[10px] font-medium rounded transition-colors capitalize ${filter === f ? "bg-cyan-500/20 text-cyan-400" : "text-[var(--text-muted)] hover:text-white"}`}
                data-testid={`filter-${f}`}>{f}</button>
            ))}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white text-xs gap-1.5" data-testid="add-entry-btn">
                <Plus size={14} /> New Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">New Journal Entry</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Symbol (e.g. RELIANCE)" value={form.symbol} onChange={e => setForm(f => ({...f, symbol: e.target.value}))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="journal-symbol" />
                  <Select value={form.action} onValueChange={v => setForm(f => ({...f, action: v}))}>
                    <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="journal-action">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input placeholder="Price" type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="journal-price" />
                  <Input placeholder="Quantity" type="number" value={form.quantity} onChange={e => setForm(f => ({...f, quantity: e.target.value}))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="journal-qty" />
                  <Input placeholder="Account" value={form.account_name} onChange={e => setForm(f => ({...f, account_name: e.target.value}))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                </div>
                <textarea placeholder="Reasoning / thesis..." value={form.reasoning} onChange={e => setForm(f => ({...f, reasoning: e.target.value}))}
                  className="w-full bg-[var(--bg-base)] border border-white/10 rounded-md p-2.5 text-sm text-white placeholder:text-[var(--text-muted)] min-h-[80px] resize-none" data-testid="journal-reasoning" />
                <Input placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                <Button onClick={handleSubmit} disabled={submitting || !form.symbol} className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white" data-testid="journal-submit">
                  {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <BookOpen size={14} className="mr-1" />} Save Entry
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Row */}
      {entries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["buy", "sell", "hold", "watch"].map(a => {
            const count = entries.filter(e => e.action === a).length;
            const cfg = ACTION_CONFIG[a];
            return (
              <div key={a} className={`${cfg.bg} border ${cfg.border} rounded-md p-3`}>
                <div className={`text-xs uppercase tracking-wider ${cfg.color} font-semibold`}>{cfg.label}</div>
                <div className="font-mono text-xl text-white">{count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Journal Entries */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen size={40} className="mx-auto text-[var(--text-muted)] mb-3 opacity-30" />
          <p className="text-sm text-[var(--text-muted)]">No journal entries yet. Click "New Entry" to start tracking your trades.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(e => {
            const cfg = ACTION_CONFIG[e.action] || ACTION_CONFIG.hold;
            const Icon = cfg.icon;
            return (
              <div key={e.id} className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4 hover:border-white/15 transition-colors" data-testid={`journal-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded ${cfg.bg} ${cfg.color} flex-shrink-0`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-white cursor-pointer hover:text-[var(--brand)]" onClick={() => onStockClick && onStockClick(e.symbol)}>{e.symbol}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase ${cfg.bg} ${cfg.color} ${cfg.border} border`}>{cfg.label}</span>
                        {e.price && <span className="text-xs font-mono text-[var(--text-secondary)]">@ {e.price.toLocaleString()}</span>}
                        {e.quantity && <span className="text-xs text-[var(--text-muted)]">{e.quantity} qty</span>}
                        {e.account_name && <span className="text-[10px] text-[var(--text-muted)] bg-white/5 px-1.5 py-0.5 rounded">{e.account_name}</span>}
                      </div>
                      {e.reasoning && <p className="text-xs text-[var(--text-secondary)] mt-1.5 leading-relaxed">{e.reasoning}</p>}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {(e.tags || []).map((t, i) => (
                          <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--text-muted)]">{t}</span>
                        ))}
                        <span className="text-[10px] text-[var(--text-muted)]">{new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {e.outcome_pct != null && (
                      <div className={`text-right ${e.outcome_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                        <div className="flex items-center gap-1 text-xs font-mono">
                          {e.outcome_pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {e.outcome_pct >= 0 ? "+" : ""}{e.outcome_pct}%
                        </div>
                        {e.current_price && <div className="text-[10px] font-mono text-[var(--text-muted)]">CMP: {e.current_price.toLocaleString()}</div>}
                      </div>
                    )}
                    <button onClick={() => handleDelete(e.id)} className="text-[var(--text-muted)] hover:text-red-400 transition-colors p-1" data-testid={`delete-${e.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
